/**
 * The /changes poll loop.
 *
 * One request every 10 seconds while the tab is visible, and it is the only thing in the
 * app that invalidates another screen's data. Individual queries do not poll on their own
 * timers except the live class.
 *
 * Every event type maps to the narrowest query key that could be affected. A
 * `metric.invalidated` event carries metric ids, so one changed number does not throw away
 * a whole screen of cached envelopes.
 */
import type { QueryClient } from '@tanstack/react-query';
import type { ChangesResponse, OpsEvent } from '@elutide/shared';
import { invalidationRoots } from '@/lib/keys';

export const CHANGES_POLL_MS = 10_000;

/**
 * The keys one batch of events wants invalidated, de-duplicated.
 *
 * This exists because of a real outage. The loop used to call `invalidateQueries` once per
 * event, and TanStack v5 defaults `cancelRefetch` to true: every call aborted the in-flight
 * fetch and started another. `lib/api.ts` passes no AbortSignal, so each cancelled request
 * still reached the server. Two hundred events in a tick became hundreds of GETs a minute
 * and 2,753 rate-limit responses. One invalidation per distinct key fixes it.
 *
 * The map key is a stable serialization of the key array, so `['metric', 'true_net']` seen
 * fifty times is one entry.
 */
class InvalidationSet {
  private readonly keys = new Map<string, readonly unknown[]>();

  add(key: readonly unknown[]): void {
    this.keys.set(JSON.stringify(key), key);
  }

  flush(client: QueryClient): void {
    for (const key of this.keys.values()) {
      void client.invalidateQueries({ queryKey: key });
    }
  }
}

/** Exported for tests: turning a batch of events into a set of invalidations. */
export function applyChangeEvents(client: QueryClient, events: readonly OpsEvent[]): void {
  const pending = new InvalidationSet();
  const invalidate = (key: readonly unknown[]) => pending.add(key);

  for (const event of events) {
    const entityId = event.entity_id;
    switch (event.type) {
      case 'metric.invalidated': {
        const ids = readMetricIds(event);
        for (const id of ids) invalidate(invalidationRoots.metric(id));
        // A screen batch key is not addressable by metric id, so it is refreshed wholesale.
        invalidate(['metrics']);
        break;
      }
      case 'order.changed':
      case 'order.writeback.failed': {
        invalidate(invalidationRoots.orders);
        if (entityId) invalidate(invalidationRoots.order(entityId));
        break;
      }
      case 'stock.changed':
      case 'product.changed': {
        invalidate(invalidationRoots.stock);
        break;
      }
      case 'customer.changed': {
        invalidate(invalidationRoots.customers);
        if (entityId) invalidate(invalidationRoots.customer(entityId));
        break;
      }
      case 'support.ticket.changed': {
        invalidate(invalidationRoots.support);
        if (entityId) invalidate(invalidationRoots.ticket(entityId));
        break;
      }
      case 'inbox.changed': {
        invalidate(invalidationRoots.inbox);
        break;
      }
      case 'job.failed': {
        invalidate(invalidationRoots.jobs);
        invalidate(invalidationRoots.health);
        break;
      }
      case 'snapshot.completed': {
        invalidate(['metrics']);
        break;
      }
      case 'audit.written': {
        invalidate(['audit']);
        break;
      }
      case 'security.event': {
        invalidate(['audit']);
        invalidate(invalidationRoots.inbox);
        break;
      }
      default:
        break;
    }
  }

  // One call per distinct key, after the whole batch. Never one per event.
  pending.flush(client);
}

function readMetricIds(event: OpsEvent): string[] {
  const raw = (event.payload as { ids?: unknown }).ids;
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === 'string');
}

/** Applies a whole /changes response: the events, then the new cursor. */
export function applyChangesResponse(
  client: QueryClient,
  response: ChangesResponse,
): number {
  applyChangeEvents(client, response.events);
  return response.cursor;
}

/* ------------------------------------------------------------------ */
/* Facts clock                                                         */
/* ------------------------------------------------------------------ */

/**
 * When the poller last heard from the server, and the facts_version it heard.
 *
 * The top bar chip reads this. It is the honest global answer to "how old is what I am
 * looking at": if the loop stops, the chip goes amber and then red rather than the page
 * quietly showing yesterday's numbers as though they were current.
 */
interface FactsClock {
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  factsVersion: Readonly<Record<string, number>>;
}

let clock: FactsClock = { lastSuccessAt: null, lastFailureAt: null, factsVersion: {} };
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribeFactsClock(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getFactsClock(): FactsClock {
  return clock;
}

export function recordChangesSuccess(factsVersion: Readonly<Record<string, number>>): void {
  clock = { ...clock, lastSuccessAt: new Date().toISOString(), factsVersion };
  emit();
}

export function recordChangesFailure(): void {
  clock = { ...clock, lastFailureAt: new Date().toISOString() };
  emit();
}

/** Test seam. */
export function resetFactsClock(): void {
  clock = { lastSuccessAt: null, lastFailureAt: null, factsVersion: {} };
  emit();
}
